import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import LandingNavBar from '../../components/LandingNavBar';
import LandingFooter from '../../components/LandingFooter';
import beds24Logo from '../../assets/images/partners/beds24.jpeg';
import staymiiLogo from '../../assets/images/staymii_logo.png';
import {
  ArrowRight, Check, Plug, Shield, Settings, UserPlus,
  Link2, RefreshCw, LifeBuoy, FileText, CalendarCheck, Globe
} from 'lucide-react';

const h = { fontFamily: "'Outfit', 'Plus Jakarta Sans', sans-serif" };
const b = { fontFamily: "'IBM Plex Sans', 'Manrope', sans-serif" };

const steps = [
  {
    num: '01',
    icon: UserPlus,
    title: 'Create your StayMii account',
    body: 'Sign up for StayMii and set up your property profile. StayMii adds a branded website, a commission-free direct booking engine and online distribution on top of your Beds24 setup.',
    points: [
      'Register for a StayMii account.',
      'Add your property name, rooms and contact details.',
      'Verify your email to activate the account.',
    ],
  },
  {
    num: '02',
    icon: Settings,
    title: 'Enable StayMii in your Beds24 account',
    body: 'In your Beds24 control panel, open (SETTINGS) → MARKETPLACE and activate StayMii. Choose the properties and room types you want StayMii to manage and distribute.',
    points: [
      'Beds24 → Settings → Marketplace → find “StayMii”.',
      'Click Activate / Enable the organisation.',
      'Select which room types StayMii can access.',
    ],
  },
  {
    num: '03',
    icon: Link2,
    title: 'Authorise the connection (invite code)',
    body: 'Beds24 uses a secure invite code to grant access — no passwords are shared. Generate the invite code in Beds24 and paste it into StayMii, or click “Connect Beds24” in StayMii and follow the prompt.',
    points: [
      'Beds24 → Settings → Marketplace/API → Generate invite code.',
      'In StayMii → Settings → Integrations → Beds24 → paste the code.',
      'StayMii confirms the connection in seconds.',
    ],
  },
  {
    num: '04',
    icon: RefreshCw,
    title: 'Sync rooms, rates & availability',
    body: 'StayMii maps your Beds24 rooms and keeps inventory, rates and availability synchronised two-way. Bookings from any connected channel update automatically, preventing overbookings.',
    points: [
      'Auto-detect and map Beds24 rooms & rate plans.',
      'Two-way sync of availability and reservations.',
      'Rates stay consistent across every channel.',
    ],
  },
  {
    num: '05',
    icon: CalendarCheck,
    title: 'Go live with direct bookings & distribution',
    body: 'Publish your StayMii direct booking engine and distribute across connected online channels and Google. Every new booking flows back into Beds24, so your calendar stays the single source of truth.',
    points: [
      'Launch the commission-free direct booking engine.',
      'Distribute to OTAs and Google Hotel listings.',
      'New bookings write back to your Beds24 calendar.',
    ],
  },
];

const StepCard = ({ s }) => {
  const Icon = s.icon;
  return (
    <div className="relative bg-white rounded-2xl border border-slate-200 p-8 md:p-10 hover:shadow-lg transition-all" data-testid={`beds24-pms-step-${s.num}`}>
      <div className="flex items-start gap-5">
        <div className="shrink-0 w-14 h-14 bg-slate-950 rounded-2xl flex items-center justify-center shadow-lg">
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <span className="text-xs font-bold tracking-[0.2em] text-cyan-600">STEP {s.num}</span>
          <h3 className="text-xl font-semibold text-slate-950 mt-1 mb-3 tracking-tight" style={h}>{s.title}</h3>
          <p className="text-slate-600 leading-relaxed mb-5">{s.body}</p>
          <ul className="space-y-2.5">
            {s.points.map((p, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <div className="w-5 h-5 bg-emerald-50 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                  <Check className="w-3 h-3 text-emerald-600" />
                </div>
                <span className="text-sm text-slate-700 leading-relaxed">{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

const Beds24PmsSetupGuide = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-white" style={b} data-testid="beds24-pms-setup-guide-page">
      <SEO
        title="How to Connect Beds24 to StayMii PMS — Setup Guide"
        description="Step-by-step guide for Beds24 customers to connect StayMii for direct bookings, channel management and two-way sync of rooms, rates and availability. Go live in minutes."
        keywords="beds24 staymii pms setup, beds24 channel manager, beds24 direct booking engine, connect beds24 staymii pms"
        canonicalUrl="/integrations/beds24/pms-setup-guide"
      />
      <LandingNavBar />

      {/* HERO */}
      <section className="relative pt-32 pb-20 overflow-hidden" data-testid="beds24-pms-guide-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-blue-50/30" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="flex items-center justify-center gap-4 mb-8">
            <img src={beds24Logo} alt="Beds24" className="h-10 w-auto rounded-lg" />
            <span className="text-2xl text-slate-300 font-light">+</span>
            <img src={staymiiLogo} alt="StayMii" className="h-10 w-auto rounded-lg" />
          </div>
          <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-blue-50 border border-blue-200 rounded-full text-xs font-bold text-blue-700 uppercase tracking-[0.15em] mb-6">
            <FileText className="w-3.5 h-3.5" /> PMS Integration Setup Guide
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-slate-950 tracking-tighter leading-tight mb-6" style={h}>
            How to connect Beds24<br />to StayMii PMS
          </h1>
          <p className="text-lg sm:text-xl text-slate-600 leading-relaxed max-w-2xl mx-auto mb-10">
            This guide shows Beds24 customers exactly what to do to activate StayMii for direct bookings,
            channel management and two-way sync of rooms, rates and availability — no migration, connect and go live in minutes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/get-started" className="group inline-flex items-center justify-center gap-2.5 px-8 py-4 bg-slate-950 hover:bg-slate-800 text-white rounded-2xl font-semibold text-base transition-all" data-testid="beds24-pms-start-cta">
              Create StayMii Account <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/integrations/beds24" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 rounded-2xl font-semibold text-base transition-all" data-testid="beds24-pms-overview">
              Integration Overview
            </Link>
          </div>
        </div>
      </section>

      {/* PREREQUISITES */}
      <section className="py-16 bg-slate-50" data-testid="beds24-pms-prereq">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="bg-white rounded-2xl border border-slate-200 p-8 md:p-10">
            <h2 className="text-2xl font-semibold text-slate-950 tracking-tight mb-5" style={h}>Before you start</h2>
            <p className="text-slate-600 leading-relaxed mb-6">You'll need the following. There is no change to your existing Beds24 plan — StayMii works alongside it.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                'An active Beds24 account with API/Marketplace access',
                'A StayMii account (free to create)',
                'Your property, room types and rate plans set up in Beds24',
                'Administrator access to your Beds24 control panel',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-blue-50 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3.5 h-3.5 text-blue-600" />
                  </div>
                  <span className="text-sm text-slate-700 font-medium leading-relaxed">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* STEPS */}
      <section className="py-20 md:py-28 bg-white" data-testid="beds24-pms-steps">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-slate-400 mb-4 block">Step-by-step</span>
            <h2 className="text-3xl sm:text-4xl font-semibold text-slate-950 tracking-tight leading-snug" style={h}>
              Five steps to go live
            </h2>
          </div>
          <div className="space-y-6">
            {steps.map((s) => <StepCard key={s.num} s={s} />)}
          </div>
        </div>
      </section>

      {/* DATA ACCESS */}
      <section className="py-16 bg-slate-50" data-testid="beds24-pms-data">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl border border-slate-200 p-8">
              <div className="w-11 h-11 bg-emerald-50 rounded-xl flex items-center justify-center mb-5">
                <Globe className="w-5 h-5 text-emerald-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-950 mb-3" style={h}>What StayMii syncs</h3>
              <ul className="space-y-2 text-sm text-slate-600">
                <li>• Rooms, rate plans and availability (two-way)</li>
                <li>• Reservations from connected channels</li>
                <li>• Booking details for confirmations & reporting</li>
              </ul>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 p-8">
              <div className="w-11 h-11 bg-amber-50 rounded-xl flex items-center justify-center mb-5">
                <Shield className="w-5 h-5 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-950 mb-3" style={h}>Safe & reversible</h3>
              <ul className="space-y-2 text-sm text-slate-600">
                <li>• Only the room types you enable are shared</li>
                <li>• No card / sensitive payment data is accessed</li>
                <li>• Disconnect anytime from Beds24 Marketplace</li>
              </ul>
            </div>
          </div>
          <p className="text-center text-xs text-slate-400 mt-6">
            Your Beds24 calendar remains the single source of truth — StayMii keeps it in sync in real time.
          </p>
        </div>
      </section>

      {/* SUPPORT */}
      <section className="py-20 md:py-28 bg-white" data-testid="beds24-pms-support">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <div className="w-14 h-14 bg-slate-950 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <LifeBuoy className="w-6 h-6 text-white" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-semibold text-slate-950 tracking-tight mb-4" style={h}>
            Need help connecting?
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed mb-8">
            Our Malaysian support team helps you activate the integration and go live. Reach us and we'll walk you through it.
          </p>
          <div className="inline-flex flex-col sm:flex-row items-center gap-3 text-sm font-medium text-slate-700 mb-10">
            <span className="inline-flex items-center gap-2"><span className="text-slate-400">Email:</span> support@staymii.com</span>
            <span className="hidden sm:inline text-slate-300">|</span>
            <span className="inline-flex items-center gap-2"><span className="text-slate-400">Phone:</span> +60 12-667 8801</span>
            <span className="hidden sm:inline text-slate-300">|</span>
            <span className="inline-flex items-center gap-2"><span className="text-slate-400">Web:</span> www.staymii.com</span>
          </div>
          <div>
            <Link to="/contact" className="group inline-flex items-center justify-center gap-2.5 px-8 py-4 bg-slate-950 hover:bg-slate-800 text-white rounded-2xl font-semibold text-base transition-all" data-testid="beds24-pms-contact-cta">
              Contact Support <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
};

export default Beds24PmsSetupGuide;
